import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import GroupWorkIcon from '@mui/icons-material/GroupWork'
import SettingsIcon from '@mui/icons-material/Settings'
import { Box, IconButton, Tab, Tabs, Tooltip } from '@mui/material'
import type { DateTime } from 'luxon'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { FormattedRow } from '@/features/bessDashboard/types'
import GroupsTabContent from '@/features/bidding/components/cascading/activation-groups/GroupsTabContent'
import ConfigurationTabContent from '@/features/bidding/components/cascading/configuration/ConfigurationTabContent'
import MarketFeatureGuard from '@/features/bidding/components/cascading/MarketFeatureGuard'
import type { RegulationObjectGroup } from '@/features/bidding/hooks/useBidOfferCascadingDerivedData'
import type { UsePricingStrategyStateReturn } from '@/features/bidding/hooks/usePricingStrategyState'
import type { BidLevelInput, DragPayload } from '@/features/bidding/types/bidLadderInput'
import type { CascadeMarketId } from '@/features/bidding/types/market'

const COLLAPSED_SIDEBAR_WIDTH = 56
const EXPANDED_SIDEBAR_PERCENTAGE = 0.35 // 35% of parent
const MAX_EXPANDED_WIDTH = 500
const SIDEBAR_EASING = 'cubic-bezier(0.32, 0.72, 0, 1)'
const SIDEBAR_TRANSITION_DURATION = 500

type BidOfferCascadingLeftSidebarProps = {
  assignedGroupUuids: Set<string>
  assignGroups: (levelId: string, payload: DragPayload) => void
  bidLevels: BidLevelInput[]
  capacityByActivationGroup: Map<string, number>
  regulationObjectGroups: RegulationObjectGroup[]
  /** Drag payload when dragging from the bid ladder (for showing drop zone in sidebar) */
  ladderDragPayload?: DragPayload | null
  /** Function to unassign a regulation object from a bid level */
  unassignRegulationObjectGroup?: (levelId: string, regulationObjectCode: string) => void
  /** Called when a drop from the ladder completes (to clear drag state) */
  onLadderDropComplete?: () => void
  /** Pricing strategy state for configuration tab */
  pricingStrategy: UsePricingStrategyStateReturn
  /** Forecast data for preview chart */
  forecastsData: FormattedRow[] | undefined
  /** Selected delivery day for forecast filtering */
  selectedDeliveryDay: DateTime
  /** Selected market to determine which content to show */
  selectedMarket: CascadeMarketId
  /** Controlled tab index (0=Groups, 1=Configuration) */
  activeTab?: number
  /** Callback when tab changes */
  onTabChange?: (tab: number) => void
  /** Controlled collapsed state */
  isCollapsed?: boolean
  /** Callback when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void
}

function BidOfferCascadingLeftSidebar({
  assignedGroupUuids,
  assignGroups,
  bidLevels,
  capacityByActivationGroup,
  regulationObjectGroups,
  ladderDragPayload,
  unassignRegulationObjectGroup,
  onLadderDropComplete,
  pricingStrategy,
  forecastsData,
  selectedDeliveryDay,
  selectedMarket,
  activeTab,
  onTabChange,
  isCollapsed,
  onCollapsedChange,
}: Readonly<BidOfferCascadingLeftSidebarProps>) {
  const { t } = useTranslation()

  // Support both controlled and uncontrolled modes for backwards compatibility
  const [internalTab, setInternalTab] = useState(0)
  const [internalCollapsed, setInternalCollapsed] = useState(false)

  // Use controlled values if provided, otherwise fall back to internal state
  const leftTab = activeTab ?? internalTab
  const isLeftSidebarCollapsed = isCollapsed ?? internalCollapsed

  const handleTabChange = (newTab: number) => {
    onTabChange?.(newTab)
    setInternalTab(newTab)
  }

  const [announceMessage, setAnnounceMessage] = useState('')
  const [enableTransition, setEnableTransition] = useState(false)

  const sidebarRef = useRef<HTMLDivElement>(null)
  const expandButtonRef = useRef<HTMLButtonElement>(null)
  const collapseButtonRef = useRef<HTMLButtonElement>(null)

  const handleCollapsedChange = (newCollapsed: boolean) => {
    setEnableTransition(true)
    onCollapsedChange?.(newCollapsed)
    setInternalCollapsed(newCollapsed)
  }

  // Tab configuration for collapsed state icons
  const leftTabConfig = [
    {
      label: t('bidding.bidding_cascading.groups_tab.tab_title'),
      icon: <GroupWorkIcon />,
    },
    {
      label: t('bidding.bidding_cascading.configuration_tab.tab_title'),
      icon: <SettingsIcon />,
    },
  ]

  // Announce state changes to screen readers
  useEffect(() => {
    if (isLeftSidebarCollapsed) {
      setAnnounceMessage(t('bidding.bidding_cascading.sidebar_collapsed'))
    } else {
      setAnnounceMessage(t('bidding.bidding_cascading.sidebar_expanded'))
    }
  }, [isLeftSidebarCollapsed, t])

  // Keyboard shortcut: [ to toggle sidebar
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger when typing in input fields
      const target = event.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
        return
      }

      if (event.key === '[') {
        event.preventDefault()
        handleCollapsedChange(!isLeftSidebarCollapsed)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isLeftSidebarCollapsed, handleCollapsedChange])

  const handleCollapse = () => {
    handleCollapsedChange(true)
    setTimeout(() => expandButtonRef.current?.focus(), SIDEBAR_TRANSITION_DURATION)
  }

  const handleExpand = () => {
    handleCollapsedChange(false)
    setTimeout(() => collapseButtonRef.current?.focus(), SIDEBAR_TRANSITION_DURATION)
  }

  return (
    <>
      {/* ARIA live region for screen reader announcements */}
      <Box
        aria-atomic="true"
        aria-live="polite"
        sx={{
          position: 'absolute',
          left: -10000,
          width: 1,
          height: 1,
          overflow: 'hidden',
        }}
      >
        {announceMessage}
      </Box>

      {/* Left Sidebar - optimized width animation with containment */}
      <Box
        ref={sidebarRef}
        sx={{
          width: isLeftSidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : `${EXPANDED_SIDEBAR_PERCENTAGE * 100}%`,
          maxWidth: isLeftSidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : MAX_EXPANDED_WIDTH,
          minWidth: COLLAPSED_SIDEBAR_WIDTH,
          borderRight: 1,
          borderColor: 'divider',
          display: 'flex',
          flexDirection: 'column',
          // Only apply transition after user has interacted to avoid animation on initial mount
          transition: enableTransition ? `width ${SIDEBAR_TRANSITION_DURATION}ms ${SIDEBAR_EASING}` : 'none',
          // Performance optimizations for width animation
          willChange: 'width',
          contain: 'layout',
          overflow: 'hidden',
        }}
      >
        {/* Collapsed state - icon tabs */}
        {isLeftSidebarCollapsed && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Tooltip placement="right" title={t('bidding.bidding_cascading.expand_sidebar')}>
              <IconButton
                ref={expandButtonRef}
                aria-expanded={false}
                aria-label={t('bidding.bidding_cascading.expand_sidebar')}
                sx={{ m: 1 }}
                onClick={handleExpand}
              >
                <ChevronRightIcon />
              </IconButton>
            </Tooltip>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 1 }}>
              {leftTabConfig.map((tab, index) => (
                <Tooltip key={tab.label} placement="right" title={tab.label}>
                  <IconButton
                    aria-label={tab.label}
                    color={leftTab === index ? 'primary' : 'default'}
                    sx={{
                      width: 40,
                      height: 40,
                      minWidth: 40,
                      minHeight: 40,
                      flexShrink: 0,
                      backgroundColor: leftTab === index ? 'action.selected' : 'transparent',
                      '&:hover': {
                        backgroundColor: leftTab === index ? 'action.selected' : 'action.hover',
                      },
                    }}
                    onClick={() => {
                      handleTabChange(index)
                      handleExpand()
                    }}
                  >
                    {tab.icon}
                  </IconButton>
                </Tooltip>
              ))}
            </Box>
          </Box>
        )}

        {/* Expanded state - horizontal tabs */}
        {!isLeftSidebarCollapsed && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              flexGrow: 1,
              minWidth: 300,
              minHeight: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                scrollButtons="auto"
                sx={{
                  flexGrow: 1,
                  minWidth: 0,
                  '& .MuiTabs-indicator': {
                    transition: 'none',
                  },
                }}
                value={leftTab}
                variant="scrollable"
                onChange={(_, newValue) => handleTabChange(newValue)}
              >
                {leftTabConfig.map((tab) => (
                  <Tab key={tab.label} label={tab.label} />
                ))}
              </Tabs>

              <Box sx={{ borderLeft: 1, borderColor: 'divider', height: 24, mx: 1 }} />

              <Tooltip title={t('bidding.bidding_cascading.collapse_sidebar')}>
                <IconButton
                  ref={collapseButtonRef}
                  aria-expanded={true}
                  aria-label={t('bidding.bidding_cascading.collapse_sidebar')}
                  sx={{ mr: 1 }}
                  onClick={handleCollapse}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </Tooltip>
            </Box>

            <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto' }}>
              {leftTab === 0 && (
                <GroupsTabContent
                  assignGroups={assignGroups}
                  assignedGroupUuids={assignedGroupUuids}
                  bidLevels={bidLevels}
                  capacityByActivationGroup={capacityByActivationGroup}
                  ladderDragPayload={ladderDragPayload}
                  regulationObjectGroups={regulationObjectGroups}
                  unassignRegulationObjectGroup={unassignRegulationObjectGroup}
                  onLadderDropComplete={onLadderDropComplete}
                />
              )}
              {leftTab === 1 && (
                <MarketFeatureGuard selectedMarket={selectedMarket}>
                  <ConfigurationTabContent
                    forecastsData={forecastsData}
                    pricingStrategy={pricingStrategy}
                    selectedDeliveryDay={selectedDeliveryDay}
                  />
                </MarketFeatureGuard>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </>
  )
}

export default BidOfferCascadingLeftSidebar
